import styled, {css} from "styled-components";
import {brand, radii} from "theme/tokens";
import type {CustomDomainTone} from "./customDomain";

/**
 * Styles for {@link ./CustomDomainSettings}, the bring-your-own-domain section of the personalization
 * sheet's Domain tab (kitecorp/transferify-ui#669). Externalized per the styling mandate.
 *
 * <p>Everything here is sized for the 600px `wide` side sheet, which becomes a full-width BOTTOM sheet
 * below 640px. That is why the record rows stack rather than forming a table: a three-column DNS table is
 * unreadable at 375px, and widening the sheet is not available — `e2e/sheetGeometry.ts` pins every
 * right-docked sheet to the right half of the 1280px baseline.
 */

export const Row = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
`;

export const FieldLabel = styled.label`
	font-size: 13px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

export const Hint = styled.span`
	font-size: 12.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const ErrorText = styled.span`
	font-size: 12.5px;
	line-height: 1.5;
	color: ${brand.error};
`;

export const DomainField = styled.div`
	display: flex;
	align-items: center;
	min-width: 0;
	flex: 1 1 240px;
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	overflow: hidden;

	&:focus-within {
		border-color: ${({theme}) => theme.tokens.focusOutline};
	}
`;

export const DomainInput = styled.input`
	flex: 1 1 auto;
	min-width: 0;
	padding: 10px 12px;
	border: 0;
	background: transparent;
	color: ${({theme}) => theme.tokens.ink};
	font-size: 14px;

	&:focus {
		outline: none;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

/** The registered host and its state word, which read as one line until the sheet gets narrow. */
export const DomainHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
	min-width: 0;
`;

export const DomainName = styled.code`
	min-width: 0;
	font-size: 14px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
	/* A host may be 253 characters, so it must wrap inside the sheet rather than widen it. */
	overflow-wrap: anywhere;
`;

/**
 * Tone drives colour only; the words come from the catalog. `neutral` is deliberately NOT the error
 * treatment — it carries "we have not switched the edge on in this environment", which is our state and
 * must not put a red badge on the owner's settings page for our own configuration.
 */
const TONE_COLORS = {
	action: brand.warning,
	progress: (theme: {tokens: {blue: string}}) => theme.tokens.blue,
	attention: brand.error,
	success: brand.live,
	neutral: (theme: {tokens: {ink2: string}}) => theme.tokens.ink2,
} as const;

function toneColor(tone: CustomDomainTone, theme: {tokens: {blue: string; ink2: string}}): string {
	const value = TONE_COLORS[tone];
	return typeof value === "function" ? value(theme) : value;
}

export const Badge = styled.span<{$tone: CustomDomainTone}>`
	flex: 0 0 auto;
	padding: 3px 9px;
	border-radius: ${radii.chip};
	font-size: 11.5px;
	font-weight: 700;
	letter-spacing: 0.02em;
	white-space: nowrap;
	color: ${({$tone, theme}) => toneColor($tone, theme)};
	border: 1px solid ${({$tone, theme}) => toneColor($tone, theme)};
	/* Tinted from the tone rather than a second palette entry, so light and dark both follow the token. */
	background: color-mix(in srgb, ${({$tone, theme}) => toneColor($tone, theme)} 12%, transparent);
`;

export const StatusText = styled.p<{$tone: CustomDomainTone}>`
	margin: 0;
	font-size: 13px;
	line-height: 1.55;
	color: ${({$tone, theme}) => ($tone === "attention" ? theme.tokens.errorInk : theme.tokens.ink2)};
`;

export const RecordsTitle = styled.h4`
	margin: 4px 0 0;
	font-size: 13px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

export const RecordList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

export const RecordCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 10px 12px;
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.card};
`;

export const RecordType = styled.span`
	align-self: flex-start;
	padding: 2px 8px;
	border-radius: ${radii.chip};
	background: ${({theme}) => theme.tokens.chip};
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.04em;
`;

/** One labelled record field: caption above, value and its copy action below. */
export const RecordField = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

export const RecordCaption = styled.span`
	font-size: 11.5px;
	font-weight: 700;
	letter-spacing: 0.02em;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const RecordRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
`;

export const RecordValue = styled.code`
	flex: 1 1 auto;
	min-width: 0;
	padding: 8px 10px;
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink};
	font-size: 12.5px;
	line-height: 1.5;
	/* A challenge value has no spaces and is long; without this it forces the whole sheet to scroll. */
	overflow-wrap: anywhere;
`;

/**
 * The secondary controls under the records. `flex-wrap` rather than a fixed row: at 375px "Check DNS"
 * and "Issue a new token" do not fit side by side, and a horizontally scrolling action bar hides the
 * control the sender came for.
 */
export const StageActions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
`;

const mutedBlock = css`
	font-size: 12.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * Shown to an owner whose plan no longer covers custom domains but who still holds a host. Reads as a
 * statement of fact rather than an upsell — the action it routes to is removal, which the api allows on
 * every tier precisely so the name can be freed.
 */
export const LapsedNote = styled.p`
	margin: 0;
	${mutedBlock};
`;
